import apiClient from './client';

/**
 * POST /quotes/simple
 * Body: { customer_id, line_items: [{product_id, quantity, unit_price}], notes?, vat_rate? }
 *
 * The PWA uses the simple endpoint — field reps quote a final per-unit USD
 * selling price directly. The backend handles VAT and produces a draft quote
 * that can be reviewed/sent on the desktop ERP. For full import-cost-calculator
 * quotes (supplier prices in ZAR, transport, insurance, customs, profit
 * margin) admins use POST /quotes on the desktop frontend.
 */
export async function createQuote(payload) {
  const response = await apiClient.post('/quotes/simple', payload);
  return response.data;
}

/**
 * GET /quotes/{id} — full QuoteOut.
 */
export async function getQuote(quoteId) {
  const response = await apiClient.get(`/quotes/${quoteId}`);
  return response.data;
}

/**
 * GET /quotes — paginated list. Backend supports filters: status, customer_id.
 * Response: { items, total, page, page_size, pages } (PaginatedResponse[QuoteListOut]).
 */
export async function listQuotes({
  page = 1,
  pageSize = 20,
  status,
} = {}) {
  const params = { page, page_size: pageSize };
  if (status) params.status = status;
  const response = await apiClient.get('/quotes', { params });
  return response.data;
}

/**
 * GET /quotes/{id}/pdf — generated on demand. Authenticated; fetch as a blob
 * via apiClient (which adds the Bearer token), then caller wraps in an
 * `URL.createObjectURL()` to view in a new tab.
 */
export async function getQuotePdfBlob(quoteId) {
  const response = await apiClient.get(`/quotes/${quoteId}/pdf`, {
    responseType: 'blob',
  });
  return response.data; // Blob
}

/**
 * PATCH /quotes/{id}/simple
 *
 * Body (any subset of): { customer_id, line_items, notes, vat_rate }.
 * line_items shape: [{ product_id, quantity, unit_price }] — same as
 * createQuote. Backend forces all import-cost fields to zero so a
 * simple update on a quote that was originally created via the full
 * desktop endpoint produces the simple-flow total (unit_price * qty,
 * plus VAT).
 *
 * Backend preconditions:
 *   - quote.status === "draft" (400 otherwise — "Only draft quotes
 *     can be edited")
 *   - require_sales_ops (sales reps pass)
 *
 * Returns the full updated QuoteOut. Drop straight into local state.
 */
export async function updateQuote(quoteId, payload) {
  const response = await apiClient.patch(`/quotes/${quoteId}/simple`, payload);
  return response.data; // QuoteOut
}

/**
 * DELETE /quotes/{id}
 *
 * Backend allows draft OR rejected quotes to be deleted; mobile UX
 * gates to draft-only for consistency with the sales delete flow
 * (rejected-delete stays on desktop). Cascade-deletes line items.
 * 204 No Content on success.
 */
export async function deleteQuote(quoteId) {
  await apiClient.delete(`/quotes/${quoteId}`);
}

/**
 * Quote lifecycle transitions — all take no body, all return the updated
 * QuoteOut so callers can drop the response straight into local state.
 *
 *   send:   draft       -> submitted  (rep marks the quote as sent to customer)
 *   accept: submitted   -> accepted   (customer agreed; backend also accepts
 *                                      direct draft -> accepted as a shortcut
 *                                      for instant on-site agreement)
 *   reject: anything except 'converted' -> rejected
 */
export async function sendQuote(quoteId) {
  const response = await apiClient.post(`/quotes/${quoteId}/send`);
  return response.data; // QuoteOut
}

export async function acceptQuote(quoteId) {
  const response = await apiClient.post(`/quotes/${quoteId}/accept`);
  return response.data;
}

export async function rejectQuote(quoteId) {
  const response = await apiClient.post(`/quotes/${quoteId}/reject`);
  return response.data;
}

/**
 * POST /quotes/{id}/convert-to-sale
 *
 * Body: { batch_lines: [{product_id, batch_id, quantity}, ...] }
 *   - non-empty list (backend rejects [])
 *   - one entry per (product, batch) pair; a single product can span
 *     multiple batches if the qty needs to be drawn from more than one
 *
 * Returns: { sale_id, invoice_number }
 *
 * Backend preconditions:
 *   - Quote must have status === "accepted"
 *   - Each batch must have available_qty >= the requested quantity
 */
export async function convertQuoteToSale(quoteId, batchLines) {
  const response = await apiClient.post(
    `/quotes/${quoteId}/convert-to-sale`,
    { batch_lines: batchLines },
  );
  return response.data; // { sale_id, invoice_number }
}
